<template>
  <div>
    <div
      class="fixed inset-0 flex z-40 h-full lg:relative lg:flex-shrink-0 lg:z-0"
      :class="{ 'hidden lg:flex': !sidebarOpen }"
    >
      <!-- Mobile menu grey background overlay-->
      <div class="fixed inset-0 lg:hidden" aria-hidden="true">
        <div class="absolute inset-0 bg-gray-600/75" />
      </div>

      <div
        class="relative flex-1 flex flex-col max-w-xs w-full pt-5 pb-4 bg-white lg:w-64 lg:border-r lg:border-gray-200"
      >
        <!-- Mobile menu close button -->
        <div class="absolute top-0 right-0 -mr-12 pt-2 lg:hidden">
          <button
            type="button"
            class="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
            @click="$emit('toggleSidebar')"
          >
            <span class="sr-only">Close sidebar</span>
            <svg
              class="h-6 w-6 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Sparklayer logo -->
        <div class="flex-shrink-0 flex items-center px-6">
          <router-link to="/">
            <img
              class="h-8 w-auto"
              src="../../assets/logo.png"
              alt="SparkLayer"
            >
          </router-link>
        </div>

        <!-- Nav items -->
        <div class="mt-5 flex-1 h-0 overflow-y-auto">
          <div class="px-3 mt-1 mb-5 relative inline-block text-left w-full">
            <!-- Dropdown menu toggle, controlling the show/hide state of dropdown menu. -->
            <div>
              <button
                v-shortkey="['meta', 'k']"
                type="button"
                class="group w-full bg-gray-100 rounded-md px-2.5 py-2.5 text-sm font-medium text-gray-700 hover:bg-indigo-100 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-purple-500"
                aria-haspopup="true"
                aria-expanded="true"
                @shortkey="toggleSiteSwitcher()"
                @click="toggleSiteSwitcher()"
              >
                <span class="flex w-full justify-between items-center">
                  <span class="flex min-w-0 items-center justify-between space-x-3">
                    <div class="rounded-full h-8 w-8 flex items-center justify-center bg-spark-purple text-white font-medium text-xs tracking-widest"><span class="w-5 overflow-hidden inline-block"> {{ acronymSiteName }} </span></div>
                    <span class="flex-1 min-w-0 text-sm font-medium max-w-xs mx-auto truncate text-ellipsis">
                      {{ siteName }}
                    </span>
                  </span>
                  <SelectorIcon
                    class="flex-shrink-0 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                  />
                </span>
              </button>
            </div>

            <div
              :class="{ hidden: !siteSwitcherOpen }"
              class="z-40 mx-3 origin-top absolute right-0 left-0 mt-1 rounded-md shadow-lg bg-white ring-1 ring-black/5"
              role="menu"
              aria-orientation="vertical"
              aria-labelledby="options-menu"
            >
              <div
                v-if="availableTeams.length > 10"
                class="p-2"
              >
                <label
                  for="search"
                  class="hidden text-sm font-medium text-gray-700"
                >Quick search</label>
                <div class="relative mt-1 flex items-center">
                  <input
                    ref="teamSearch"
                    v-model="siteSwitcherSearch"
                    type="text"
                    name="search"
                    class="block w-full rounded-md border-gray-300 pr-12 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                  <div class="absolute inset-y-0 right-0 flex py-1.5 pr-1.5">
                    <kbd class="inline-flex items-center rounded border border-gray-200 px-2 font-sans text-sm font-medium text-gray-400">⌘K</kbd>
                  </div>
                </div>
              </div>
              <div class="py-1">
                <div
                  v-for="item in teams"
                  :key="item.id"
                  class=""
                >
                  <button
                    type="button"
                    :class="{ 'bg-purple-50 hover:bg-purple-200': item.is_partner }"
                    class="block relative w-full text-left px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 text-ellipsis overflow-hidden"
                    role="menuitem"
                    @click="changeSite(item.id)"
                  >
                    {{ item.name }}
                    <span
                      v-if="item.is_partner"
                    >
                      <GlobeAltIcon class="absolute top-2.5 right-2 h-4 w-4 text-purple-600" />
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Nav links -->
          <nav class="px-3 divide-y divide-gray-200">
            <div
              v-if="isPartner"
            >
              <div class="space-y-1 text-gray-600">
                <div
                  v-for="partnerNavItem in partnerNav"
                  :key="partnerNavItem.href"
                >
                  <router-link
                    :to="partnerNavItem.href"
                    active-class="text-indigo-600"
                    class="hover:text-indigo-600 group flex items-center px-2 py-2 text-sm font-medium rounded-md"
                  >
                    <component
                      :is="partnerNavItem.icon"
                      class="group-hover:text-spark-purple mr-3 h-6 w-6"
                    />
                    {{ partnerNavItem.title }}
                  </router-link>
                  <div v-if="partnerNavItem.subNav.length && $route.matched.some(({ path }) => path.startsWith(partnerNavItem.href))" class="relative">
                    <span
                      class="bg-gray-500 opacity-80 w-[1px] h-[calc(100%-8px)] block absolute top-0 left-[17px] z-20"
                    />
                    <router-link
                      v-for="subNavItem in partnerNavItem.subNav"
                      :key="subNavItem.href"
                      :to="subNavItem.href"
                      active-class="text-indigo-600 font-medium"
                      class="hover:text-indigo-600 hover:bg-gray-50 group transition-all flex items-center p-1 text-sm rounded-md relative z-1 cursor-pointer"
                    >
                      <ArrowRightIcon
                        class="mr-2.5 ml-[12px] h-3.5 w-3.5 text-gray-400 opacity-0"
                      />
                      {{ subNavItem.title }}
                    </router-link>
                  </div>
                </div>

                <a
                  href="#"
                  class="text-gray-600 hover:text-spark-purple group flex items-center px-2 py-2 text-sm font-medium rounded-md"
                  @click="toggleCoreNav"
                >
                  <HomeIcon
                    class="group-hover:text-spark-purple mr-3 h-6 w-6"
                  />
                  Test Mode
                </a>
                <span class="text-xs px-2 font-normal text-gray-500 mt-2.5 pb-2 block">Free sandbox account</span>
              </div>
            </div>
            <div
              v-if="!isPartner || showCoreNav"
              class="space-y-0 text-gray-600 relative"
            >
              <div
                v-if="isPartner"
                class="bg-indigo-50 h-[105%] w-full absolute left-0 top-0"
              >
                &nbsp;
              </div>
              <div v-for="coreNavItem in coreNav" :key="coreNavItem.href">
                <router-link
                  :to="coreNavItem.href"
                  active-class="text-indigo-600 bg-indigo-50 hover:bg-indigo-50"
                  class="hover:text-indigo-600 hover:bg-gray-50 group transition-all flex items-center px-2 py-2 text-sm font-medium rounded-md relative z-1"
                >
                  <component
                    :is="coreNavItem.icon"
                    class="group-hover:text-indigo-600 mr-2.5 h-5 w-5"
                  />
                  {{ coreNavItem.title }}
                </router-link>
                <div v-if="coreNavItem.subNav.length && $route.matched.some(({ path }) => path.startsWith(coreNavItem.href))" class="relative">
                  <span
                    class="bg-gray-500 opacity-80 w-[1px] h-[calc(100%-8px)] block absolute top-0 left-[17px] z-20"
                  />
                  <router-link
                    v-for="subNavItem in coreNavItem.subNav"
                    :key="subNavItem.href"
                    :to="subNavItem.href"
                    active-class="text-indigo-600 font-medium"
                    class="hover:text-indigo-600 hover:bg-gray-50 group transition-all flex items-center p-1 text-sm rounded-md relative z-1 cursor-pointer"
                  >
                    <ArrowRightIcon
                      class="mr-2.5 ml-[12px] h-3.5 w-3.5 text-gray-400 opacity-0"
                    />
                    {{ subNavItem.title }}
                  </router-link>
                </div>
              </div>
            </div>
            <div class="mt-4 pt-4">
              <div class="space-y-0 text-gray-600">
                <router-link
                  to="/settings"
                  active-class="text-indigo-600 bg-indigo-50 hover:bg-indigo-50"
                  class="hover:text-indigo-600 hover:bg-gray-50 transition-all group flex items-center px-2 py-2 text-sm font-medium rounded-md"
                >
                  <UserCircleIcon class="mr-2.5 h-5 w-5 group-hover:text-spark-purple" />
                  Account
                </router-link>
                <div v-if="$route.matched.some(({ path }) => path.startsWith('/settings'))" class="relative">
                  <span
                    class="bg-gray-500 opacity-80 w-[1px] h-[calc(100%-8px)] block absolute top-0 left-[17px] z-20"
                  />
                  <router-link
                    to="/settings/account"
                    active-class="text-indigo-600 font-medium"
                    class="hover:text-indigo-600 hover:bg-gray-50 group transition-all flex items-center p-1 text-sm rounded-md relative z-1 cursor-pointer"
                  >
                    <ArrowRightIcon
                      class="mr-2.5 ml-[12px] h-3.5 w-3.5 text-gray-400 opacity-0"
                    />
                    My Account
                  </router-link>
                  <router-link
                    to="/settings/plan"
                    active-class="text-indigo-600 font-medium"
                    class="hover:text-indigo-600 hover:bg-gray-50 group transition-all flex items-center p-1 text-sm rounded-md relative z-1 cursor-pointer "
                  >
                    <ArrowRightIcon
                      class="mr-2.5 ml-[12px] h-3.5 w-3.5 text-gray-400 opacity-0"
                    />
                    Plan
                  </router-link>
                  <router-link
                    to="/settings/password"
                    active-class="text-indigo-600 font-medium"
                    class="hover:text-indigo-600 hover:bg-gray-50 group transition-all flex items-center p-1 text-sm rounded-md relative z-1 cursor-pointer "
                  >
                    <ArrowRightIcon
                      class="mr-2.5 ml-[12px] h-3.5 w-3.5 text-gray-400 opacity-0"
                    />
                    Password
                  </router-link>
                  <router-link
                    to="/settings/team"
                    active-class="text-indigo-600 font-medium"
                    class="hover:text-indigo-600 hover:bg-gray-50 group transition-all flex items-center p-1 text-sm rounded-md relative z-1 cursor-pointer "
                  >
                    <ArrowRightIcon
                      class="mr-2.5 ml-[12px] h-3.5 w-3.5 text-gray-400 opacity-0"
                    />
                    Team
                  </router-link>
                </div>
                <router-link
                  to="/help"
                  active-class="text-indigo-600 bg-indigo-50 hover:bg-indigo-50"
                  class="hover:text-indigo-600 hover:bg-gray-50 transition-all group flex items-center px-2 py-2 text-sm font-medium rounded-md"
                >
                  <QuestionMarkCircleIcon class="mr-2.5 h-5 w-5 group-hover:text-spark-purple" />
                  Help
                </router-link>
                <a
                  v-if="shopifyDomain"
                  :href="shopifyDomain"
                  class="hover:text-indigo-600 hover:bg-gray-50 transition-all group flex items-center px-2 py-2 text-sm font-medium rounded-md"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg
                    width="75"
                    height="85"
                    class="text-gray-600 mr-2.5 h-5 w-5 group-hover:text-spark-purple"
                    stroke="none"
                    viewBox="0 0 75 85"
                    fill="#777"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M50.823 9.83771C50.8164 9.8399 49.8806 10.1292 48.2982 10.6202C48.0352 9.76321 47.645 8.709 47.0905 7.65041C45.3021 4.23796 42.6809 2.42983 39.5161 2.42544C39.5117 2.42544 39.5095 2.42544 39.5051 2.42544C39.2859 2.42544 39.0668 2.44515 38.8476 2.46488C38.7556 2.3531 38.6613 2.24133 38.5605 2.13613C37.1819 0.661122 35.411 -0.0577457 33.2939 0.00362146C29.2042 0.121972 25.132 3.07419 21.8314 8.31889C19.506 12.0097 17.7417 16.6473 17.2398 20.2351C12.5452 21.6882 9.25987 22.7073 9.18973 22.7292C6.82052 23.4722 6.746 23.5467 6.43478 25.78C6.20027 27.4698 0 75.4217 0 75.4217L51.4476 84.3199V9.73251C51.1934 9.75005 50.9654 9.79607 50.823 9.83771ZM38.944 13.5176C36.222 14.3614 33.25 15.2819 30.2672 16.2046C31.1066 12.9937 32.6955 9.79607 34.6483 7.70082C35.3738 6.92277 36.3907 6.05269 37.594 5.55518C38.7271 7.91343 38.9703 11.2579 38.944 13.5176ZM33.3728
                  2.7257C34.3327 2.70598 35.1415 2.91637 35.8319 3.37005C34.7272 3.94427 33.6577 4.76835 32.6561 5.84227C30.059 8.6279 28.0689 12.9543 27.2755 17.1273C24.7989 17.8943 22.3771 18.6439 20.1503 19.3343C21.5552 12.7658 27.0607 2.90761 33.3728 2.7257ZM25.417 40.1465C25.6931 44.5233 37.2082 45.4789 37.8548 55.7316C38.3632 63.797 33.5766 69.3134 26.6794 69.7496C18.4014 70.2712 13.8449 65.3882 13.8449 65.3882L15.5982 57.9255C15.5982 57.9255 20.1854 61.3861 23.8565 61.1538C26.2542 61.0026 27.1111 59.052 27.0257 57.6712C26.6662 51.9619 17.2902 52.2972 16.6963 42.9168C16.1987 35.0224 21.3821 27.0227 32.8227 26.2994C37.2302 26.0211 39.4876 27.1454 39.4876 27.1454L36.8707 36.9313C36.8707 36.9313 33.9536 35.6032 30.4929 35.8201C25.4192 36.1445 25.3644 39.3444 25.417 40.1465ZM41.6617 12.6738C41.631 10.6026 41.3834 7.72273 40.419 5.23298C43.5225 5.82035 45.0501 9.33143 45.6944 11.4245C44.5 11.7971 43.1389 12.2179 41.6617 12.6738Z"
                    />
                    <path
                      d="M53.1484 84.1161L74.491 78.81C74.491 78.81 65.3013 16.6802 65.2443 16.2528C65.1851 15.8276 64.8125 15.5909 64.5057 15.5646C64.1989 15.5383 58.1893 15.4484 58.1893 15.4484C58.1893 15.4484 54.5248 11.8913 53.1484 10.5434V84.1161Z"
                    />
                  </svg>
                  Return to Shopify
                </a>
              </div>
            </div>

            <!-- Environment switcher -->
            <div
              v-if="showEnvironmentSelector"
              class="mt-6 pt-6"
            >
              <button
                type="button"
                class="flex items-center px-2"
                @click="switchEnvironment"
              >
                <div
                  class="relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-400"
                  :class="[isLive ? 'bg-gray-200' : 'bg-yellow-400']"
                >
                  <span class="sr-only">Use setting</span>
                  <span
                    class="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"
                    :class="[isLive ? 'translate-x-' : 'translate-x-5']"
                  />
                </div>
                <span
                  class="ml-3"
                >
                  <span
                    class="text-sm font-medium text-gray-600 hover:text-spark-purple cursor-pointer"
                  >Test mode</span>
                </span>
              </button>

              <span class="text-xs px-2 font-normal text-gray-500 mt-2.5 block">Load data from
                <a
                  href="https://docs.sparklayer.io/test-mode"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center text-gray-600 hover:text-indigo-500"
                >
                  test environment
                </a>
              </span>
            </div>
          </nav>
        </div>
      </div>

      <!-- Dummy element to force sidebar to shrink to fit close icon -->
      <div
        class="flex-shrink-0 w-14 lg:hidden"
        aria-hidden="true"
      />
    </div>
  </div>
</template>

<script>

import {
  ArrowRightIcon,
  BellIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ClipboardListIcon,
  RefreshIcon,
  CodeIcon,
  CogIcon,
  GlobeAltIcon,
  HomeIcon,
  LogoutIcon,
  MenuAlt1Icon,
  QuestionMarkCircleIcon,
  ShoppingCartIcon,
  SwitchVerticalIcon,
  UserCircleIcon,
  UsersIcon,
} from '@heroicons/vue/outline';
import { mapActions, mapGetters } from 'vuex';
import { SelectorIcon } from '@heroicons/vue/solid/index';
import { sortBy } from 'lodash';
import { nextTick } from 'vue';

export default {
  components: {
    HomeIcon,
    CodeIcon,
    ShoppingCartIcon,
    ClipboardListIcon,
    RefreshIcon,
    UsersIcon,
    UserCircleIcon,
    BellIcon,
    SwitchVerticalIcon,
    LogoutIcon,
    CogIcon,
    QuestionMarkCircleIcon,
    ArrowRightIcon,
    GlobeAltIcon,
    CheckCircleIcon,
    MenuAlt1Icon,
    ChevronDownIcon,
    SelectorIcon,
  },
  props: {
    sidebarOpen: {
      type: Boolean,
      required: true,
    },
  },
  emits: [
    'toggleSidebar',
  ],
  data() {
    return {
      siteSwitcherOpen: false,
      siteSwitcherSearch: '',
      partnerNav: [
        {
          href: '/partners',
          title: 'Partner Dashboard',
          icon: GlobeAltIcon,
          subNav: [
            {
              href: '/partners/overview',
              title: 'Overview',
            },
            {
              href: '/partners/details',
              title: 'Referrals',
            },
            {
              href: '/partners/downloads',
              title: 'Resources',
            },
            {
              href: '/settings/team',
              title: 'Team',
            },
          ],
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      acronymName: 'auth/acronymName',
      siteName: 'auth/siteName',
      acronymSiteName: 'auth/acronymSiteName',
      availableTeams: 'auth/availableTeams',
      isAdvancedFeaturesEnabled: 'auth/isAdvancedFeaturesEnabled',
      isPartner: 'auth/isPartner',
      isLive: 'env/isLive',
      team: 'auth/team',
      env: 'env/get',
      showCoreNav: 'partner/showCoreNav',
    }),
    coreNav() {
      const nav = [
        {
          href: '/',
          title: 'Home',
          icon: HomeIcon,
          subNav: [],
        },
        {
          href: '/orders',
          title: 'Orders',
          icon: ShoppingCartIcon,
          subNav: [],
        },
        {
          href: '/products',
          title: 'Price Lists',
          icon: ClipboardListIcon,
          subNav: [
            {
              href: '/products/price-lists',
              title: 'Price Lists',
            },
            {
              href: '/products/price-editor',
              title: 'Price Editor',
            },
          ],
        },
        {
          href: '/customers',
          title: 'Customer Groups',
          icon: UsersIcon,
          subNav: [],
        },
        {
          href: '/data-sync',
          title: 'Data Sync',
          icon: RefreshIcon,
          subNav: [
            {
              href: '/data-sync/products',
              title: 'Product Sync',
            },
            {
              href: '/data-sync/customers',
              title: 'Customer Sync',
            },
          ],
        },
        {
          href: '/configuration',
          title: 'Configuration',
          icon: CogIcon,
          subNav: [
            {
              href: '/configuration/onboarding',
              title: 'Onboarding & Setup',
            },
            {
              href: '/configuration/api',
              title: 'API Keys',
              show: this.isAdvancedFeaturesEnabled,
            },
            {
              href: '/configuration/integrations',
              title: 'Integrations',
            },
            {
              href: '/configuration/frontend',
              title: 'Frontend',
            },
            {
              href: '/configuration/shipping',
              title: 'Shipping',
            },
            {
              href: '/configuration/settings',
              title: 'Settings',
            },
          ],
        },
      ];

      const navFiltered = nav.filter((item) => item.show !== false);

      return navFiltered.map((item) => ({
        ...item,
        subNav: item.subNav.filter((subItem) => subItem.show !== false),
      }));
    },
    teams() {
      const teams = Array.isArray(this.availableTeams) ? this.availableTeams : [];
      return sortBy(
        teams.filter((x) => x.name.toLowerCase()
          .includes(this.siteSwitcherSearch.toLowerCase())) ?? [],
        (x) => !x.is_partner,
      );
    },
    showEnvironmentSelector() {
      return this.isAdvancedFeaturesEnabled && !this.isPartner;
    },
    shopifyDomain() {
      if (!(this.env?.shopify_site_url)) {
        return '';
      }
      return `https://${this.env?.shopify_site_url}/admin/apps/sparklayer`;
    },
  },
  methods: {
    ...mapActions({
      toggleCoreNav: 'partner/toggleCoreNav',
    }),
    async switchEnvironment() {
      await this.$store.dispatch('env/loadEnvironment', this.isLive ? 'test' : 'live');
    },
    async changeSite(teamId) {
      await this.$router.push(`/switch-account/${teamId}`);
      this.siteSwitcherOpen = false;
    },
    async toggleSiteSwitcher() {
      this.siteSwitcherOpen = !this.siteSwitcherOpen;
      // Team search if only available for accounts with more than 10 teams
      if (this.teams.length < 10) {
        return;
      }
      // Focus the input, but this is needed after the re-render of the sidebar!
      await nextTick();
      this.focusTeamSearchInput();
    },
    focusTeamSearchInput() {
      this.$refs.teamSearch.focus();
    },
  },
};
</script>
